import { DomainError } from "../domain/entities.js";
import { anchorOf, endOf, keyOf, parseHorizonKey, shift } from "../horizon/horizon.js";
import { stampNew, stampUpdate } from "./context.js";
/** Крок порядку. REAL навмисно: вставка між сусідами не переписує решту рядків. */
const ORDINAL_STEP = 1000;
function requireHorizon(key) {
    const h = parseHorizonKey(key);
    if (!h)
        throw new DomainError(`«${key}» не є ключем горизонту`);
    return h;
}
export async function listPlan(ctx, horizonKey) {
    const h = requireHorizon(horizonKey);
    const row = await ctx.horizons.find(h.kind, h.key);
    if (!row)
        return []; // горизонт створюється ліниво — порожній ще не існує
    const items = await ctx.horizons.itemsOf(row.id);
    return items.filter((i) => !i.deletedAt).sort((a, b) => a.ordinal - b.ordinal);
}
/**
 * План за проміжок горизонтів одного масштабу — для сітки місяця й
 * тижневого розвороту. Ключі беруться включно з обома межами.
 */
export async function listPlanRange(ctx, kind, fromKey, toKey) {
    const rows = await ctx.horizons.itemsInRange(kind, fromKey, toKey);
    const out = new Map();
    for (const { horizonKey, item } of rows) {
        if (item.deletedAt)
            continue;
        const list = out.get(horizonKey);
        if (list)
            list.push(item);
        else
            out.set(horizonKey, [item]);
    }
    for (const list of out.values())
        list.sort((a, b) => a.ordinal - b.ordinal);
    return out;
}
/**
 * Лічильники місяців проміжку — «зроблено з усього», без самих справ.
 *
 * Рік показує дванадцять таких пар і жодного рядка плану, тож і читає він
 * числа. Перелік означав би розшифрування кожної справи року заради того,
 * щоб її порахувати й викинути, — і не раз на відкриття, а на кожній
 * галочці, бо кожна перемальовує лічильники.
 *
 * Місяцю належать його власні наміри й наміри його днів. **Тижні свідомо
 * не рахуються**: тиждень регулярно лежить у двох місяцях одразу, і
 * зарахувати його в будь-який означало б збрехати рівно в тому числі,
 * заради якого лічильник існує.
 *
 * Ключ дня починається з ключа місяця (`2026-03-07` → `2026-03`) — формат
 * ключів зафіксовано в docs/03, і на це вже спирається сітка місяця.
 */
export async function countPlanByMonth(ctx, span) {
    const first = anchorOf(span);
    const last = endOf(span);
    const [byMonth, byDay] = await Promise.all([
        ctx.horizons.countsInRange("month", keyOf("month", first), keyOf("month", last)),
        ctx.horizons.countsInRange("day", keyOf("day", first), keyOf("day", last)),
    ]);
    const out = new Map();
    const add = (horizonKey, c) => {
        const at = out.get(horizonKey);
        out.set(horizonKey, {
            horizonKey,
            done: (at?.done ?? 0) + c.done,
            total: (at?.total ?? 0) + c.total,
        });
    };
    for (const c of byMonth)
        add(c.horizonKey, c);
    for (const c of byDay)
        add(c.horizonKey.slice(0, 7), c);
    return out;
}
export async function addIntent(ctx, horizonKey, text) {
    if (!text.trim())
        throw new DomainError("порожній намір не зберігається");
    const h = requireHorizon(horizonKey);
    const row = await ctx.horizons.ensure(h.kind, h.key);
    const existing = await ctx.horizons.itemsOf(row.id);
    const last = existing.reduce((m, i) => Math.max(m, i.ordinal), 0);
    const item = {
        id: ctx.ids.next(),
        horizonId: row.id,
        text: text.trim(),
        doneAt: null,
        ordinal: last + ORDINAL_STEP,
        rolledFromId: null,
        spanEndKey: null,
        ...stampNew(ctx),
    };
    await ctx.horizons.saveItem(item);
    return item;
}
/**
 * Виконане лишається видимим: чекбокс — це стан, а не видалення.
 * Повторний виклик знімає позначку.
 */
export async function toggleIntent(ctx, id) {
    const item = await ctx.horizons.itemById(id);
    if (!item || item.deletedAt)
        throw new DomainError(`наміру ${id} немає`);
    const next = stampUpdate(ctx, { ...item, doneAt: item.doneAt ? null : ctx.clock.now() });
    await ctx.horizons.saveItem(next);
    return next;
}
/**
 * Правка тексту пункту — поле вгорі B2 «Властивості пункту» (картка SheetPlan).
 * Власного екрана в пункту немає: текст правиться тут, решта — рядками шторки.
 */
export async function renameIntent(ctx, id, text) {
    if (!text.trim())
        throw new DomainError("порожній намір не зберігається");
    const item = await ctx.horizons.itemById(id);
    if (!item || item.deletedAt)
        throw new DomainError(`наміру ${id} немає`);
    const next = stampUpdate(ctx, { ...item, text: text.trim() });
    await ctx.horizons.saveItem(next);
    return next;
}
/** «ГГ:ХХ», 00:00–23:59. Секунд і зон немає: це час на сторінці дня. */
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
/**
 * Час пункту — B2 → «Час пункту» (SheetTime): початок і, за бажанням,
 * кінець. `null` — «Без часу»: пункт перестає бути подією.
 *
 * Лише для пунктів **дня**: у тижня чи місяця немає години, на яку цей час
 * указував би. `calendarEventId` не чіпається — саме за ним клієнт знайде
 * подію, щоб її посунути або прибрати.
 */
export async function timeIntent(ctx, id, time) {
    const item = await ctx.horizons.itemById(id);
    if (!item || item.deletedAt)
        throw new DomainError(`наміру ${id} немає`);
    if (time !== null) {
        const row = await ctx.horizons.byId(item.horizonId);
        if (row?.kind !== "day")
            throw new DomainError("час буває лише в пункту дня");
        if (!TIME.test(time.start))
            throw new DomainError(`«${time.start}» — не час`);
        if (time.end !== null && !TIME.test(time.end))
            throw new DomainError(`«${time.end}» — не час`);
        // Рядкове порівняння чесне: «ГГ:ХХ» з провідними нулями впорядковане лексично.
        if (time.end !== null && time.end <= time.start)
            throw new DomainError("кінець раніше за початок");
    }
    const next = stampUpdate(ctx, { ...item, startTime: time?.start ?? null, endTime: time?.end ?? null });
    await ctx.horizons.saveItem(next);
    return next;
}
/** Звʼязати пункт із подією Google Календаря або розвʼязати (`null`). */
export async function linkCalendarEvent(ctx, id, eventId) {
    const item = await ctx.horizons.itemById(id);
    if (!item)
        throw new DomainError(`наміру ${id} немає`);
    const next = stampUpdate(ctx, { ...item, calendarEventId: eventId });
    await ctx.horizons.saveItem(next);
    return next;
}
/**
 * Протяжність — «Триває: − 1 день +» у B2. Кінець зберігається ключем того
 * самого масштабу; одна одиниця (сам проміжок) — це `null`, а не ключ самого
 * себе: так пункт без протяжності не відрізняється від старих рядків.
 */
export async function spanIntent(ctx, id, units) {
    if (!Number.isInteger(units) || units < 1)
        throw new DomainError("протяжність — ціле число від 1");
    const item = await ctx.horizons.itemById(id);
    if (!item || item.deletedAt)
        throw new DomainError(`наміру ${id} немає`);
    const row = await ctx.horizons.byId(item.horizonId);
    if (!row)
        throw new DomainError("горизонт наміру зник — дані пошкоджені");
    const end = units === 1 ? null : shift({ kind: row.kind, key: row.key }, units - 1).key;
    const next = stampUpdate(ctx, { ...item, spanEndKey: end });
    await ctx.horizons.saveItem(next);
    return next;
}
/**
 * Видалити пункт — «Видалити» внизу B2, після підтвердження D1. Мʼяке, бо
 * синхронізації потрібен tombstone: інакше пункт воскресне з іншого пристрою.
 */
export async function deleteIntent(ctx, id) {
    const item = await ctx.horizons.itemById(id);
    if (!item || item.deletedAt)
        throw new DomainError(`наміру ${id} немає`);
    await ctx.horizons.saveItem(stampUpdate(ctx, { ...item, deletedAt: ctx.clock.now() }));
}
/**
 * Скільки разів намір переносили. Це і є те, що показує маркер `→N`:
 * `→3` поруч із «закрити курсову» повідомляє те, чого не скаже жоден список справ.
 */
/** Довжини ланцюгів для набору рядків — одним запитом. */
export async function rollCounts(ctx, ids) {
    return ctx.horizons.rollCounts(ids);
}
export async function rollCount(ctx, id) {
    return (await ctx.horizons.rollCounts([id])).get(id) ?? 0;
}
//# sourceMappingURL=plan.js.map